import { Badge } from "@/components/ui/badge";
import { ACCESS_CLASS_BG } from "@/lib/constants";
import { ShieldCheck, Library } from "lucide-react";

const GROUP_ORDER = ["Scientific Literature", "Regulatory & HTA", "Congress & Scientific Meetings", "Education & Programs", "Expert & Field Intelligence", "Digital & Media", "Partner & Client Intelligence"];

export const GovernancePanel = ({ data }) => {
  const groups = GROUP_ORDER.filter((g) => data.categories.some((c) => c.group === g));
  return (
    <div className="space-y-6">
      <div className="bg-slate-900 rounded-xl p-6 border border-slate-800 flex gap-4 items-start" data-testid="compliance-banner">
        <div className="w-10 h-10 rounded-lg bg-teal-500/20 border border-teal-500/40 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-teal-400" />
        </div>
        <div>
          <h3 className="font-heading text-base font-bold text-white">Source Governance & Compliance</h3>
          <p className="text-sm text-slate-400 mt-1 leading-relaxed max-w-3xl">
            All intelligence is collected exclusively from publicly available, legitimately accessed, licensed, commissioned or client-provided sources.
            The platform does not bypass paywalls, does not download restricted content without permission, and does not access private conversations.
            Every source item carries an explicit access classification, and AI-generated syntheses are always labelled as AI-derived interpretation.
          </p>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl p-6 border border-slate-800 flex gap-4 items-start" data-testid="privacy-banner">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-indigo-400" />
        </div>
        <div>
          <h3 className="font-heading text-base font-bold text-white">HCP Identity & Privacy Model</h3>
          <p className="text-sm text-slate-400 mt-1 leading-relaxed max-w-3xl">
            HCP Voice uses a hybrid identity model. The principal intelligence layer is the aggregated, synthesised collective HCP voice; individual statements serve as supporting evidence.
            Named attribution is retained only where the statement comes from a publicly accessible, legitimately usable source and attribution is legally and ethically appropriate.
            The system never infers private views. Direct quotations are always distinguished from AI-generated paraphrases.
            Administrators can anonymise any speaker at any time, and every named statement remains traceable to its original source.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="classification-legend">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Access classifications · insight counts in current dataset</p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(ACCESS_CLASS_BG).map((cls) => (
            <Badge key={cls} variant="outline" className={`text-xs font-semibold ${ACCESS_CLASS_BG[cls]}`} data-testid={`class-badge-${cls.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {cls}
              <span className="ml-1.5 font-mono opacity-70">{cls === "AI-derived interpretation" ? "console" : (data.classification_counts[cls] || 0)}</span>
            </Badge>
          ))}
        </div>
      </div>

      {groups.map((g) => (
        <div key={g}>
          <div className="flex items-center gap-2 mb-3">
            <Library className="w-4 h-4 text-teal-600" />
            <h3 className="font-heading text-base font-bold text-slate-900">{g}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.categories.filter((c) => c.group === g).map((c) => (
              <div key={c.id} data-testid={`source-category-${c.id}`} className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-100 hover:border-teal-400 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{c.name}</p>
                  <span className="font-mono text-xs text-slate-400 shrink-0">{c.volume}</span>
                </div>
                <Badge variant="outline" className={`mt-2 text-[10px] font-semibold ${ACCESS_CLASS_BG[c.classification]}`}>{c.classification}</Badge>
                <p className="text-xs text-slate-500 mt-2 leading-snug">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default GovernancePanel;
